import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { JournalVoucher5Service } from "services/journal-voucher-5";
import { CompanyService } from "services/companyService";
import { formatVndIntegerValue } from "utils";
import { getPrintTemplate } from "./components/printTemplate";

const defaultYear = String(new Date().getFullYear());
const defaultMonth = String(new Date().getMonth() + 1);

const ui = {
  title: { fontSize: 20, lineHeight: 1.2 },
  table: { fontSize: 13 },
  th: { border: "1px solid #000", padding: 6, textAlign: "center", fontSize: 12, backgroundColor: "#f9f9f9" },
  td: { border: "1px solid #000", padding: 6, fontSize: 12 },
  tdCenter: { border: "1px solid #000", padding: 6, textAlign: "center", fontSize: 12 },
  tdRight: { border: "1px solid #000", padding: 6, textAlign: "right", fontSize: 12 },
};

const fmt = (n) => (n && n !== 0 ? new Intl.NumberFormat("vi-VN").format(n) : "");

function JournalVoucher5Detail() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [companyId, setCompanyId] = useState("");
  const [companyInfo, setCompanyInfo] = useState({ name: "", address: "" });
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({
    year: defaultYear,
    month: defaultMonth,
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || "");
    setFilters({
      year: searchParams.get("year") || defaultYear,
      month: searchParams.get("month") || defaultMonth,
    });
  }, [location.search]);

  useEffect(() => {
    const init = async () => {
      try {
        const response = await CompanyService.getByAuth();
        const payload = response?.data || response || {};
        setCompanyId(payload?._id || "");
        setCompanyInfo({
          name: payload?.name || payload?.companyName || payload?.unit || "",
          address: payload?.address || payload?.companyAddress || "",
        });
      } catch (error) {
        console.error("Lỗi lấy thông tin công ty:", error);
      }
    };
    init();
  }, []);

  const fetchData = useCallback(async () => {
    if (!companyId || !filters.year || !filters.month) return;
    try {
      setLoading(true);
      const response = await JournalVoucher5Service.preview({
        company: companyId,
        year: filters.year,
        month: filters.month,
      });
      setData(response?.data || response || null);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Không thể tải dữ liệu Nhật ký chứng từ số 5");
    } finally {
      setLoading(false);
    }
  }, [companyId, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePrint = () => {
    if (!data) return;
    const printValues = {
      ...data,
      unit: companyInfo.name,
      address: companyInfo.address,
      month: filters.month,
      year: filters.year,
      signingDay: new Date().getDate().toString(),
      signingMonth: (new Date().getMonth() + 1).toString(),
      signingYear: new Date().getFullYear().toString(),
    };
    const html = getPrintTemplate(printValues);
    const win = window.open("", "_blank");
    win.document.open();
    win.document.write(`<html><head><title>In S04a5-DN</title>
      <style>@media print{@page{size:A4 landscape;margin:1cm}}body{margin:0}</style>
      </head><body onload="setTimeout(()=>[window.print(),window.close()], 500)">${html}</body></html>`);
    win.document.close();
  };

  const handleBack = () => navigate("/templates/journal-voucher-5");

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card sx={{ overflow: "visible" }}>
              <SoftBox p={3}>
                <SoftBox mb={2}>
                  <SoftTypography variant="h5" fontWeight="bold" sx={ui.title}>
                    CHI TIẾT NHẬT KÝ CHỨNG TỪ SỐ 5 (S04a5-DN)
                  </SoftTypography>
                </SoftBox>

                <SoftBox border="1px solid #000" p={3} sx={{ fontFamily: "'Times New Roman', serif", color: "#000", backgroundColor: "#fff" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                    <div style={{ width: "40%" }}>
                      <div style={{ fontSize: 14 }}><strong>Đơn vị:</strong> {companyInfo.name}</div>
                      <div style={{ fontSize: 14 }}><strong>Địa chỉ:</strong> {companyInfo.address}</div>
                    </div>
                    <div style={{ textAlign: "center", width: "60%" }}>
                      <div style={{ fontWeight: "bold", fontSize: "15px" }}>Mẫu số S04a5-DN</div>
                      <div style={{ fontStyle: "italic", fontSize: "11px" }}>(Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</div>
                    </div>
                  </div>

                  <div style={{ textAlign: "center", margin: "10px 0" }}>
                    <h2 style={{ margin: 0, textTransform: "uppercase", fontSize: "18px" }}>NHẬT KÝ CHỨNG TỪ SỐ 5</h2>
                    <div style={{ fontSize: "14px", fontStyle: "italic", marginTop: "5px" }}>
                        Ghi Có Tài khoản 331 - Phải trả cho người bán
                    </div>
                    <div style={{ marginTop: 10, fontWeight: "bold" }}>
                      Tháng {filters.month} năm {filters.year}
                    </div>
                  </div>

                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1200px", ...ui.table }}>
                      <thead>
                        <tr>
                          <th style={{ ...ui.th, width: "40px" }} rowSpan={2}>Số TT</th>
                          <th style={ui.th} colSpan={2}>Chứng từ</th>
                          <th style={{ ...ui.th, width: "200px" }} rowSpan={2}>Diễn giải</th>
                          <th style={ui.th} colSpan={10}>Ghi Nợ các Tài khoản đối ứng</th>
                          <th style={{ ...ui.th, width: "100px" }} rowSpan={2}>Tổng cộng ghi Có TK 331</th>
                        </tr>
                        <tr>
                          <th style={{ ...ui.th, width: "80px" }}>Số hiệu</th>
                          <th style={{ ...ui.th, width: "80px" }}>Ngày</th>
                          {[...Array(10)].map((_, i) => (
                            <th key={i} style={{ ...ui.th, width: "80px" }}>
                              {data?.debitAccountLabels?.[i] || ""}
                            </th>
                          ))}
                        </tr>
                        <tr style={{ backgroundColor: "#f0f0f0" }}>
                          <th style={ui.th}>A</th>
                          <th style={ui.th}>B</th>
                          <th style={ui.th}>C</th>
                          <th style={ui.th}>D</th>
                          {[...Array(10)].map((_, i) => <th key={i} style={ui.th}>{i + 1}</th>)}
                          <th style={ui.th}>11</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data?.rows?.length > 0 ? (
                          data.rows.map((row, idx) => (
                            <tr key={idx}>
                              <td style={ui.tdCenter}>{idx + 1}</td>
                              <td style={ui.tdCenter}>{row.voucherNumber}</td>
                              <td style={ui.tdCenter}>{row.voucherDate}</td>
                              <td style={ui.td}>{row.description}</td>
                              {[...Array(10)].map((_, i) => (
                                <td key={i} style={ui.tdRight}>{fmt(row.creditValues?.[i])}</td>
                              ))}
                              <td style={{ ...ui.tdRight, backgroundColor: "#eee", fontWeight: "bold" }}>{fmt(row.totalCredit331)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={15} style={ui.tdCenter}>Không có dữ liệu</td>
                          </tr>
                        )}
                        <tr style={{ fontWeight: "bold", backgroundColor: "#fafafa" }}>
                          <td style={ui.tdCenter} colSpan={4}>Cộng</td>
                          {[...Array(10)].map((_, i) => (
                            <td key={i} style={ui.tdRight}>{fmt(data?.totals?.creditValues?.[i])}</td>
                          ))}
                          <td style={ui.tdRight}>{fmt(data?.totals?.totalCredit331)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </SoftBox>
              </SoftBox>

              <SoftBox px={3} pb={3} display="flex" justifyContent="flex-end" gap={2}>
                <SoftButton variant="outlined" color="dark" onClick={handleBack}>
                  Quay lại
                </SoftButton>
                <SoftButton variant="gradient" color="success" onClick={handlePrint} disabled={!data}>
                  In Bảng
                </SoftButton>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default JournalVoucher5Detail;
